import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { articleJsonLd, siteUrl } from "@/data/seo";

const homeLink = <Link to="/" className="text-primary-600 hover:underline" />;

function FaqItem({ question, children }: { question: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-background-300/60 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
        aria-expanded={open}
      >
        <span className="font-heading text-base font-semibold text-foreground-950">{question}</span>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-500">
          <i className={`text-lg transition-transform ${open ? "rotate-45" : ""} ri-add-line`} />
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm leading-relaxed text-foreground-700">{children}</div>
      )}
    </div>
  );
}

export default function ArticlePage() {
  const { t } = useTranslation();

  const headers = t("article.comparison.headers").split("|");
  const rows = [
    t("article.comparison.row1").split("|"),
    t("article.comparison.row2").split("|"),
    t("article.comparison.row3").split("|"),
    t("article.comparison.row4").split("|"),
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={articleJsonLd({
          headline: t("article.title"),
          description: t("article.subtitle"),
          url: siteUrl("/article"),
        })}
      />
      <Header />
      <main className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            {t("article.breadcrumbHome")}
          </Link>
          <span className="text-foreground-300">/</span>
          <span className="text-foreground-700">{t("article.breadcrumbCurrent")}</span>
        </nav>

        <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
          {t("article.title")}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
          {t("article.subtitle")}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
          <span className="flex items-center gap-2">
            <i className="ri-calendar-line" />
            {t("article.updated")}
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-time-line" />
            6 {t("article.readTime")}
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-shield-check-line text-accent-600" />
            USFanslink
          </span>
        </div>

        <article className="mt-10 space-y-10">
          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.intro.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">
              <Trans i18nKey="article.intro.p1" components={{ 1: homeLink }} />
            </p>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.intro.p2")}</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.what.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.what.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.what.p2")}</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.how.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.how.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">
              <Trans i18nKey="article.how.p2" components={{ 1: homeLink }} />
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.directory.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.directory.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.directory.p2")}</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.search.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.search.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">
              <Trans i18nKey="article.search.p2" components={{ 1: homeLink }} />
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.reviews.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.reviews.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.reviews.p2")}</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.shipping.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.shipping.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">
              <Trans i18nKey="article.shipping.p2" components={{ 1: homeLink }} />
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.comparison.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.comparison.p1")}</p>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-300/60">
              <table className="w-full min-w-[480px] border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {headers.map((h) => (
                      <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-950">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t border-background-300/60">
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`whitespace-nowrap px-4 py-3 ${
                            j === 0 ? "font-medium text-foreground-800" : "text-foreground-600"
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.faq.title")}
            </h2>
            <div className="mt-5 space-y-3">
              <FaqItem question={t("article.faq.q1")}>
                <Trans i18nKey="article.faq.a1" components={{ 1: homeLink }} />
              </FaqItem>
              <FaqItem question={t("article.faq.q2")}>
                {t("article.faq.a2")}
              </FaqItem>
              <FaqItem question={t("article.faq.q3")}>
                {t("article.faq.a3")}
              </FaqItem>
              <FaqItem question={t("article.faq.q4")}>
                {t("article.faq.a4")}
              </FaqItem>
              <FaqItem question={t("article.faq.q5")}>
                <Trans i18nKey="article.faq.a5" components={{ 1: homeLink }} />
              </FaqItem>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.conclusion.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{t("article.conclusion.p1")}</p>
            <p className="mt-3 leading-relaxed text-foreground-700">
              <Trans i18nKey="article.conclusion.p2" components={{ 1: homeLink }} />
            </p>
          </section>
        </article>

        <div className="mt-12 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
          <p className="font-heading text-lg font-semibold text-foreground-950">
            {t("article.ctaTitle")}
          </p>
          <p className="mt-2 text-sm text-foreground-600">{t("article.ctaText")}</p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("article.breadcrumbHome")}
            <i className="ri-arrow-left-line text-base" />
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}